import { Injectable, TemplateRef } from '@angular/core';

import { LocationsService } from '../../../../services/locations.service';
import { isPostcodeSearch, RegionTally, SearchHit } from '../map/map-queries';
import { HoverInfo, MapMode, MapTool, OVERVIEW, SuburbMapEngine } from '../map/suburb-map.engine';
import { SubRegionAction } from '../rules/suburb-subregion-edits';
import { TemplateKey } from '../template/suburb-template';
import { ExtraToolId } from '../tools/extra-tools';
import { Panel, WorkspaceView } from './panel';
import { StatusToast, statusToast } from './status-toast';
import { MapFilter } from './workspace-keys';
import { WorkspaceTab } from './workspace-tabs';

/** One saved map action the server can undo (its batch), and what its toast calls it. */
export interface UndoStep {
  batchId: string;
  label: string;
}

/** The workspace's own "are you sure" (a save that would remove suburbs from pages), answered in its dialog. */
export interface ConfirmRequest {
  message: string;
  resolve: (ok: boolean) => void;
}

/**
 * The Suburbs workspace's state (#256, #273), in one place, for one open popup (provided by the workspace component):
 * which tab, view and panel are showing, whether the left menu is open and the panel widened, the map's mode, tool,
 * active region and sub-region and filters, the undo stack, the last status (shown as a toast), and the tallies. The
 * panels, the left menu and the map read and change it here.
 *
 * The map's tool settings (mode, tool, active region and sub-region, filters) live in the engine, which applies them
 * as it draws and edits; the accessors here are the one way in, and redraw the map as the settings change.
 *
 * Redrawing the popup: the app coalesces change detection into animation frames (main.ts, for iPad tap latency), and
 * work that finishes outside Angular's zone, keyboard shortcuts and the map's own events don't reliably get a pass of
 * their own here; so the services call redraw(), which the workspace component binds to its change detector.
 */
@Injectable()
export class WorkspaceStateService {
  /** The map, once the workspace has attached it (MapHostService.attach). */
  engine!: SuburbMapEngine;
  /** The top tab (#273): Suburbs (its stage `view`), Page Templates (`templateKey`), Extra Tools (`extraTool`). */
  tab: WorkspaceTab = 'suburbs';
  view: WorkspaceView = 'map';
  templateKey: TemplateKey = 'suburb';
  /** The Extra Tool in the main panel (#279); null when the person sees none. */
  extraTool: ExtraToolId | null = null;
  panel: Panel | null = null;
  /** The left menu is open (its button folds it to a thin rail, for more of the main panel). */
  menuOpen = true;
  /** The side panel is widened to the whole main panel. */
  panelExpanded = false;
  /** Widened, with a block preview showing (#274): the preview covers the main panel instead of the panel. */
  widePreview = false;
  stateCode = 'nsw';
  loading = true;
  loadError = '';
  /** Editing switched off (nothing sets it today: the touch-screen rule was dropped, Ivan 2026-10-16). */
  readOnly = false;
  /**
   * The open editor's actions (Save, Publish…), drawn in the side panel's header beside its title (Ivan 2026-11-03: the
   * footer took two rows of a small panel). Set by the editor, cleared when it closes.
   */
  panelActions: TemplateRef<unknown> | null = null;
  /** The last status message, and whether it was an error (each also shows as a toast: status-toast.ts). */
  status = 'Loading…';
  statusError = false;
  /** Saves queued or in flight. */
  saving = 0;
  undoStack: UndoStep[] = [];
  hover: HoverInfo | null = null;
  confirm: ConfirmRequest | null = null;
  /** Assigned and covered suburbs per region, members per sub-region, and how many of the loaded state are assigned. */
  counts = new Map<string, RegionTally>();
  memberCounts = new Map<string, number>();
  here = { assigned: 0, total: 0 };
  searchText = '';
  results: SearchHit[] = [];
  /** A postcode search's suburbs, from the server (#282), and the text they answer. */
  private postcodeHits: { text: string; codes: string[] } | null = null;

  private redrawView: (() => void) | null = null;
  private showToast: ((t: StatusToast) => void) | null = null;

  constructor(private locations: LocationsService) {}

  /** The workspace component's change detection, for redraw(). */
  bindRedraw(redraw: () => void): void {
    this.redrawView = redraw;
  }

  unbindRedraw(): void {
    this.redrawView = null;
  }

  /** The workspace component's toasts, for setStatus(). */
  bindToasts(show: ((t: StatusToast) => void) | null): void {
    this.showToast = show;
  }

  /** Redraw the popup now (see the class comment). A no-op once the popup is gone. */
  redraw(): void {
    this.redrawView?.();
  }

  /**
   * Say what happened: an error stays as a toast until closed; a save or a note shows briefly (status-toast.ts).
   * `undo`: a map edit's save, whose toast offers Undo (what it takes back).
   */
  setStatus(message: string, error = false, undo: string | null = null): void {
    this.status = message;
    this.statusError = error;
    const toast = statusToast(message, error, undo);
    if (toast) this.showToast?.(toast);
  }

  /** Ask in the workspace's own dialog; resolves when it's answered. */
  ask(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirm = { message, resolve };
      this.redraw();
    });
  }

  answer(ok: boolean): void {
    const c = this.confirm;
    this.confirm = null;
    c?.resolve(ok);
  }

  /** The assignments changed: recount, and rerun the search so its rows say where each suburb stands now. */
  mapChanged(): void {
    if (!this.engine) return;
    this.counts = this.engine.regionCounts();
    this.memberCounts = this.engine.subRegionCounts();
    this.here = this.engine.inLoadedState();
    if (this.searchText) this.results = this.searchedHere();
  }

  /** A name searches the map's copy; a postcode (or its start) asks the server, which has the postcodes. */
  search(): void {
    const text = this.searchText.trim();
    this.results = this.searchedHere();
    if (!isPostcodeSearch(text) || this.postcodeHits?.text === text) return;
    this.locations.suburbs(text).subscribe({
      next: (hits) => {
        if (this.searchText.trim() !== text) return;
        this.postcodeHits = { text, codes: hits.map((h) => h.salCode) };
        this.results = this.searchedHere();
        this.redraw();
      },
      error: () => {},
    });
  }

  private searchedHere(): SearchHit[] {
    const text = this.searchText.trim();
    if (!isPostcodeSearch(text)) return this.engine.search(this.searchText);
    return this.postcodeHits?.text === text
      ? this.engine.searchByCodes(this.postcodeHits.codes)
      : [];
  }

  /** The all-regions review is showing (or loading): read-only. */
  get overview(): boolean {
    return this.stateCode === OVERVIEW;
  }

  /** The suburb whose panel is open. */
  get selectedCode(): string | null {
    return this.panel?.kind === 'suburb' ? this.panel.salCode : null;
  }

  /** The selected suburb's live state on the map (the engine is the source of truth while editing). */
  get selectedArea(): { regionId: string | null; covered: boolean; subRegions: string[] } {
    const a = this.selectedCode ? this.engine.areas.get(this.selectedCode) : undefined;
    return {
      regionId: a?.regionId ?? null,
      covered: !!a?.covered,
      subRegions: a?.subRegions ?? [],
    };
  }

  // ------------------------------------------------------------------ the map's tool settings (held by the engine)

  get mode(): MapMode {
    return this.engine?.mode ?? 'view';
  }
  set mode(m: MapMode) {
    this.engine.mode = m;
    this.engine.syncToolCursor();
    this.engine.render(); // the active sub-region's fill shows in Sub-region mode only
  }

  get tool(): MapTool {
    return this.engine?.tool ?? 'pointer';
  }
  set tool(t: MapTool) {
    this.engine.tool = t;
    this.engine.syncToolCursor();
  }

  get activeRegion(): string | null {
    return this.engine?.activeRegion ?? null;
  }
  set activeRegion(id: string | null) {
    this.engine.activeRegion = id; // resets Assign mode's sub-region to None
    this.engine.render();
  }

  get activeSubRegion(): string | null {
    return this.engine?.activeSubRegion ?? null;
  }
  set activeSubRegion(id: string | null) {
    this.engine.activeSubRegion = id;
    this.engine.render();
  }

  /**
   * Assign mode's Sub-region picker (#260, Ivan 2026-10-21): '' = None (region only), else one of the active region's
   * sub-regions that assigned suburbs join too. The engine resets it when the active region changes; it lives only as
   * long as the tool is open.
   */
  get assignSubRegion(): string {
    return this.engine?.assignSubRegionRef?.id ?? '';
  }
  set assignSubRegion(id: string) {
    this.engine.assignSubRegion = id || null;
    this.engine.render(); // the picked sub-region's outline is drawn thicker, with a light fill
  }

  get subRegionAction(): SubRegionAction {
    return this.engine?.subRegionAction ?? 'add';
  }
  set subRegionAction(a: SubRegionAction) {
    this.engine.subRegionAction = a;
  }

  isFilterOn(filter: MapFilter): boolean {
    if (filter === 'labels') return this.engine?.showLabels ?? false;
    if (filter === 'subregions') return this.engine?.showSubRegions ?? false;
    return this.engine?.unassignedOnly ?? false;
  }

  setFilter(filter: MapFilter, on: boolean): void {
    if (filter === 'labels') this.engine.showLabels = on;
    else if (filter === 'subregions') this.engine.showSubRegions = on;
    else this.engine.unassignedOnly = on;
    this.engine.render();
    this.mapChanged();
  }

  /** What the Regions filter hides (#273): regions' fills, and sub-regions' outlines. */
  get hiddenOnMap(): { regions: Set<string>; subRegions: Set<string> } {
    return {
      regions: this.engine?.hiddenRegions ?? new Set(),
      subRegions: this.engine?.hiddenSubRegions ?? new Set(),
    };
  }

  setHiddenOnMap(hidden: { regions: Set<string>; subRegions: Set<string> }): void {
    this.engine.hiddenRegions = new Set(hidden.regions);
    this.engine.hiddenSubRegions = new Set(hidden.subRegions);
    this.engine.render();
    this.mapChanged();
  }
}
